import React, { useCallback, useMemo, useState } from "react";

import {
  createQColumnHelper,
  DataProvider,
  QBox,
  QCard,
  QCenter,
  QCollapse,
  QDataTable,
  QHeading,
  QIcon,
  QIconButton,
  QStack,
  QTag,
  QTooltip,
} from "@qualio/ui-components";
import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { FeatureFlags, RequirementDesignElement } from "@design-controls/types";
import { Link, useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { designElementStatusColumn } from "../DesignElementStatusColumn/DesignElementStatusColumn";
import { sortByCode } from "../../sortByCode";

const columnHelper = createQColumnHelper<RequirementDesignElement>();

type DesignElement = { latest?: string } & RequirementDesignElement;

type Props = {
  requirements: Array<DesignElement>;
  underReview?: boolean;
  versioned?: boolean;
};

export const RequirementTable: React.FC<Props> = ({
  requirements,
  underReview,
  versioned = false,
}) => {
  const configs = useCurrentConfigs();
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const features = useFlags();

  const onClickToDiff = useCallback(
    (item: DesignElement) => {
      return () => navigate(`${item.id}`);
    },
    [navigate],
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.code("code", {
        header: "Code",
        minWidth: "100px",
        width: "10%",
      }),
      {
        accessorKey: "title",
        header: "Title",
        meta: {
          minWidth: "300px",
          width: "30%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: RequirementDesignElement };
        }) => {
          return (
            <Link
              to={`../requirement/${row.id}${
                versioned ? `/version/${row.version}` : ""
              }`}
              target="_blank"
            >
              {row.title}
            </Link>
          );
        },
      },
      columnHelper.text((record) => record.component?.label, {
        header: "Component",
        minWidth: "150px",
        width: "10%",
      }),
      columnHelper.text((record) => record.category?.label, {
        header: "Category",
        minWidth: "150px",
        width: "10%",
      }),
      {
        header: "Type",
        meta: {
          minWidth: "150px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: RequirementDesignElement };
        }) => {
          return (
            <QTag variantColor="gray">
              {
                (
                  configs.find((config) => config.type === row.type) as
                    | { label: string }
                    | undefined
                )?.label
              }
            </QTag>
          );
        },
      },
      {
        ...designElementStatusColumn(),
        meta: {
          minWidth: "150px",
          width: "10%",
        },
      },
    ];

    if (versioned && features[FeatureFlags.COMPARE_VIEW]) {
      columns.push({
        header: " ",
        id: "diff",
        meta: {
          minWidth: "50px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return (
            row.itemStatus !== "NEW" && (
              <QCenter>
                <QTooltip label="See difference">
                  <QBox>
                    <QIconButton
                      iconName="GitPullRequest"
                      aria-label="See difference between version"
                      onClick={onClickToDiff(row)}
                    />
                  </QBox>
                </QTooltip>
              </QCenter>
            )
          );
        },
      });
    }

    if (underReview) {
      columns.push({
        header: " ",
        meta: {
          minWidth: "90px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return row.latest && row.version !== row.latest ? (
            <QCenter>
              <QIcon
                iconName="FillExclamationTriangleFill"
                color="orange.500"
              />
            </QCenter>
          ) : null;
        },
      });
    }

    return columns;
  }, [underReview, versioned, onClickToDiff, features]);

  if (!requirements.length) {
    return null;
  }

  return (
    <QCard>
      <QStack
        direction="row"
        justifyContent="space-between"
        padding="12px 16px"
      >
        <QHeading fontSize="md" fontWeight={600}>
          Requirements
        </QHeading>
        <QIconButton
          onClick={() => setExpanded((s) => !s)}
          iconName={expanded ? "ChevronUp" : "ChevronDown"}
          aria-label={
            expanded ? "Collapse requirements" : "Expand requirements"
          }
        />
      </QStack>
      <QCollapse isOpen={expanded}>
        <DataProvider.Fixed data={requirements.sort(sortByCode)}>
          <QDataTable
            columns={columns}
            hideItemCount
            data-cy="requirement-list"
          />
        </DataProvider.Fixed>
      </QCollapse>
    </QCard>
  );
};
